<script lang="ts">
  import { onDestroy, onMount } from "svelte";
  import {
    canReplaceExercises,
    errorTracker,
    replaceExercise,
    translate,
  } from "lib";
  import {
    alertStore,
    dialogData,
    trainerExercisesStore,
    user,
  } from "stores";
  import { ButtonComponent, InfiniteScroll, Search, TemplateTagComponent } from "ui";
  import ExercisesFilter from "../../Training/ExercisesFilter.svelte";

  /**
   * Replace exercise dialog. Two internal stages:
   *   PICK    — pick the replacement (search list, prefilled with old name)
   *   CONFIRM — destructive confirmation before firing the request
   *
   * Reuses trainerExercisesStore so the picker behaves identically to the
   * Vežbe screen list (filter, search, infinite scroll). The store's filter
   * is snapshotted on mount and restored on destroy so the screen behind the
   * dialog isn't left with our prefilled search.
   *
   * Permission gating: both this dialog and the menu item that opens it call
   * canReplaceExercises($user). If a non-allowed user somehow ended up here,
   * the dialog refuses to open and closes itself.
   */

  // Loose typing matches the convention used by other dialogs in this folder
  // (ImportExercise, ConfirmDelete, etc.) — Dialog.svelte passes `$dialogData.data`
  // which TypeScript widens to `object`/`{}`.
  let data: any;
  export { data };

  type Stage = "PICK" | "CONFIRM";

  const oldExercise = data?.exercise;

  let stage: Stage = "PICK";
  let pickedExercise: any = null;       // row tapped in the picker
  let selectedExercise: any = null;     // confirmed selection, moves to CONFIRM
  let isReplacing = false;
  let scrollElement: HTMLDivElement;

  // Snapshot the previous filter so we can restore it when the dialog closes.
  // trainerExercisesStore is shared with the Vežbe screen, and we don't want
  // our prefilled search to leak back into it.
  const previousFilter = {
    search: $trainerExercisesStore.filter.search,
    sort: $trainerExercisesStore.filter.sort,
    template: $trainerExercisesStore.filter.template,
    tags: [...$trainerExercisesStore.filter.tags],
  };

  // Exclude the exercise being replaced from its own results.
  $: filteredItems = $trainerExercisesStore.items.filter(
    (e: any) => e.id !== oldExercise?.id
  );

  const onPick = (exercise: any) => {
    pickedExercise = exercise;
  };

  const onChooseAndReplace = () => {
    if (!pickedExercise) return;
    selectedExercise = pickedExercise;
    stage = "CONFIRM";
  };

  const onChangeSelection = () => {
    selectedExercise = null;
    stage = "PICK";
  };

  const closeDialog = () => {
    $dialogData.type = "";
    $dialogData.data = {};
  };

  const onConfirmReplace = async () => {
    if (isReplacing || !selectedExercise) return;

    // Defense-in-depth — the menu hides this for non-allowed users, but
    // refuse to fire the request from here too. The visible UI is the gate,
    // this helper is the gate, both must agree.
    if (!canReplaceExercises($user)) {
      alertStore.show(translate("ERROR_REPLACING_EXERCISE"), "error");
      errorTracker.captureError("ReplaceExercise: blocked by client-side allowlist", "warning", {
        context: "training",
        action: "replace_exercise",
        userId: $user?.id,
      });
      return;
    }

    isReplacing = true;

    try {
      const response = await replaceExercise(oldExercise.id, selectedExercise.id);

      // The API returns { data, error }. Treat any non-null error as a
      // failure regardless of HTTP status — same convention as the rest of
      // this API.
      if (!response || response.error || !response.data) {
        const message = response?.error || translate("ERROR_REPLACING_EXERCISE");
        alertStore.show(message, "error");
        errorTracker.captureError("ReplaceExercise: API returned error", "warning", {
          context: "training",
          action: "replace_exercise",
          oldExerciseId: oldExercise.id,
          newExerciseId: selectedExercise.id,
          message,
        });

        // For "old/new not found" the list is stale — refresh it so the
        // deleted-by-someone-else row disappears.
        if (
          typeof message === "string" &&
          (message.includes("not found") || message.includes("Unauthorized"))
        ) {
          await trainerExercisesStore.fetchData();
        }

        isReplacing = false;
        return;
      }

      const { movedReferences } = response.data;
      const count =
        (movedReferences?.exerciseParent || 0) +
        (movedReferences?.statsParent || 0) +
        (movedReferences?.cantDoParent || 0);

      // Drop the deleted row from the cached list and refetch so the
      // server is the source of truth.
      trainerExercisesStore.remove([oldExercise.id]);
      await trainerExercisesStore.fetchData();

      alertStore.show(
        translate("EXERCISE_REPLACED_TOAST").replace("{count}", String(count))
      );

      closeDialog();
    } catch (error) {
      alertStore.show(translate("ERROR_REPLACING_EXERCISE"), "error");
      errorTracker.captureError(error as Error | string, "error", {
        context: "training",
        action: "replace_exercise",
        oldExerciseId: oldExercise?.id,
        newExerciseId: selectedExercise?.id,
      });
    } finally {
      isReplacing = false;
    }
  };

  const mountScrollElement = (element: HTMLDivElement): void => {
    scrollElement = element;
  };

  onMount(async () => {
    // Guard: if a non-allowed user somehow opens this dialog, close it.
    if (!canReplaceExercises($user)) {
      closeDialog();
      return;
    }

    // Prefill the search bar with the old exercise name so the trainer can
    // find the correct replacement fast.
    $trainerExercisesStore.filter.search = oldExercise?.name || "";
    await trainerExercisesStore.fetchData();
  });

  onDestroy(() => {
    // Restore the previous filter state so the screen behind the dialog
    // isn't left with our prefilled search.
    $trainerExercisesStore.filter.search = previousFilter.search;
    $trainerExercisesStore.filter.sort = previousFilter.sort;
    $trainerExercisesStore.filter.template = previousFilter.template;
    $trainerExercisesStore.filter.tags = previousFilter.tags;
    trainerExercisesStore.fetchData();
  });
</script>

<div class="p-4 flex flex-col gap-4">
  <div class="text-center font-semibold">
    {translate("REPLACE_EXERCISE")}
  </div>

  <!-- Header strip: always visible so the trainer doesn't lose track of which
       exercise they're replacing. -->
  <div
    class="flex items-center gap-2 border rounded-md bg-slate-50 dark:bg-zinc-700 dark:border-zinc-600 p-2"
  >
    <img
      class="h-12 rounded-l-md"
      src={`https://i.ytimg.com/vi/${oldExercise?.youtubeId}/mqdefault.jpg`}
      alt="Old exercise"
    />
    <div class="flex flex-col text-xs">
      <span class="text-slate-500 dark:text-slate-300">
        {translate("REPLACE_EXERCISE_PICKER_HEADER")}
      </span>
      <span class="font-semibold">{oldExercise?.name}</span>
    </div>
  </div>

  {#if stage === "PICK"}
    <Search
      placeholder={translate("SEARCH_EXERCISES")}
      bind:value={$trainerExercisesStore.filter.search}
      on:input={trainerExercisesStore.search}
    />

    <ExercisesFilter />

    <div class="h-64 flex flex-col gap-2 overflow-y-scroll" use:mountScrollElement>
      {#each filteredItems as exercise (exercise.id)}
        <!-- svelte-ignore a11y-click-events-have-key-events -->
        <!-- svelte-ignore a11y-no-static-element-interactions -->
        <div
          class="relative selectable-exercise flex items-center gap-2 border rounded-md cursor-pointer
            {pickedExercise?.id === exercise.id
              ? 'bg-primary-100 dark:bg-primary-900 border-primary-500'
              : 'bg-slate-100 dark:bg-zinc-800 dark:border-zinc-500'}"
          on:click={() => onPick(exercise)}
        >
          {#if exercise.isTemplate}
            <TemplateTagComponent />
          {/if}
          <img
            class="h-12 rounded-l-md"
            src={`https://i.ytimg.com/vi/${exercise.youtubeId}/mqdefault.jpg`}
            alt="Replacement candidate"
          />
          <div class="text-xs">
            <div>{exercise.name}</div>
            {#if exercise.type}
              <div>{exercise.type}</div>
            {/if}
          </div>
        </div>
      {/each}

      <InfiniteScroll
        {scrollElement}
        hasMore={$trainerExercisesStore.hasMore}
        isFetchingMore={$trainerExercisesStore.isFetchingMore}
        on:loadMore={trainerExercisesStore.fetchMoreData}
      />
    </div>

    <div class="flex justify-center gap-2">
      <ButtonComponent isOutline on:click={closeDialog}>
        {translate("CANCEL")}
      </ButtonComponent>
      <ButtonComponent disabled={!pickedExercise} on:click={onChooseAndReplace}>
        {translate("CHOOSE_AND_REPLACE")}
      </ButtonComponent>
    </div>
  {:else if stage === "CONFIRM"}
    <div class="flex flex-col gap-3">
      <div class="text-center text-sm">
        {translate("REPLACE_EXERCISE")}
        <span class="font-semibold">"{oldExercise?.name}"</span>
        →
        <span class="font-semibold">"{selectedExercise?.name}"</span>?
      </div>

      <div class="flex items-center gap-2 border rounded-md p-2 dark:border-zinc-600">
        <img
          class="h-12 rounded-l-md"
          src={`https://i.ytimg.com/vi/${selectedExercise?.youtubeId}/mqdefault.jpg`}
          alt="New exercise"
        />
        <div class="text-xs">
          <div class="font-semibold">{selectedExercise?.name}</div>
          {#if selectedExercise?.type}
            <div>{selectedExercise.type}</div>
          {/if}
        </div>
      </div>

      <div class="text-xs text-slate-600 dark:text-slate-300">
        {translate("CONFIRM_REPLACE_EXERCISE_BODY")}
      </div>

      <div class="flex justify-center gap-2">
        <ButtonComponent isOutline disabled={isReplacing} on:click={onChangeSelection}>
          {translate("CANCEL")}
        </ButtonComponent>
        <ButtonComponent
          customColor="bg-red-500 hover:bg-red-800 focus:ring-red-800"
          disabled={isReplacing}
          isLoading={isReplacing}
          on:click={onConfirmReplace}
        >
          {translate("REPLACE")}
        </ButtonComponent>
      </div>
    </div>
  {/if}
</div>
